import { BlogCard } from './types';

export const sameDayGpBlogCards: BlogCard[] = [
  {
    title: {
      content:
        '### Same-Day Private GP Appointment in London: No Referral, No Waiting List',
      styles: {
        h3: 'font-Mulish font-bold text-royal-875 my-3 text-base md:text-lg leading-tight line-clamp-2',
      },
      topicsStyles: {
        h3: 'text-theme-primary text-3xl md:text-4xl my-4 md:my-6 p-2 font-black',
      },
    },
    description: {
      content:
        'A same-day GP appointment in London gives you access to a qualified private doctor on the day you call, with no referral, no prior registration, and no waiting list.',
      styles: {
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
      },
    },
    imageUrl: 'same-day-private-gp.webp',
    bannerUrl: 'gp-blog-cover-3.webp',
    bannerDescription:
      'A same-day GP appointment in London gives you access to a qualified private doctor on the day you call, with no referral, no prior registration, and no waiting list. LIPS Healthcare offers confirmed same-day GP slots at Battersea Power Station, with on-site blood tests, private prescriptions, and same-day specialist referrals available from the same visit. This guide explains how same-day private GP appointments work, what they can treat, and how to book one in London today.',
    slug: 'same-day-private-gp-appointment-london',
    contentType: 'article',
    speciality: 'GP - General Practice',
    date: {
      content: '15, September 2026',
      iconUrl: '/images/blogs/calendar.svg',
      styles: {
        p: 'text-gray-500 text-xs',
      },
    },
    author: {
      content: 'LIPS Healthcare Marketing Team',
      styles: {
        p: 'text-indigo-650 font-bold text-xs',
        h3: 'text-theme-primary font-black text-2xl md:text-4xl',
      },
    },
    tableOfContents: {
      heading: {
        content: '### TABLE OF CONTENTS:',
        styles: {
          h3: 'font-Mulish font-bold text-royal-875 mb-3 text-lg md:text-xl',
        },
      },
      content: [
        {
          text: 'What is a same-day GP appointment and how do you book one in London?',
          id: 'what-is-a-same-day-gp-appointment',
        },
        {
          text: 'What can a same-day GP appointment treat?',
          id: 'what-can-a-same-day-gp-appointment-treat',
        },
        {
          text: 'Same-day GP vs walk-in GP: what is the difference?',
          id: 'same-day-gp-vs-walk-in-gp',
        },
        {
          text: 'Need to see a GP today but cannot travel? Same-day telephone, video, and home visits are available',
          id: 'same-day-telephone-video-home-visits',
        },
        {
          text: 'Where to get a same-day private GP appointment in South London',
          id: 'where-to-get-a-same-day-private-gp-appointment',
        },
        {
          text: 'Frequently asked questions about same-day GP appointments in London',
          id: 'frequently-asked-questions',
        },
      ],
      styles: {
        p: 'text-lg text-teal-500 underline leading-snug text-left cursor-pointer font-semibold',
      },
    },
    content: {
      content: `

### Same-Day Private GP Appointment in London: No Referral, No Waiting List

A same-day GP appointment in London gives you access to a qualified private doctor on the day you call, with no referral, no prior registration, and no waiting list. LIPS Healthcare offers confirmed same-day GP slots at Battersea Power Station, with on-site blood tests, private prescriptions, and same-day specialist referrals available from the same visit. This guide explains how same-day private GP appointments work, what they can treat, and how to book one in London today.

<div align="center">\n\n
![Same-day private GP appointment in London](private-gp-appointment-cost.webp "600x400")\n\n
</div>

<span id="what-is-a-same-day-gp-appointment"></span>

### What is a same-day GP appointment and how do you book one in London?

A same-day GP appointment gives you a confirmed slot with a qualified doctor on the day you contact the clinic. There is no registration process to complete in advance, and no GP referral is required. At LIPS Healthcare in Turbine Hall B, Battersea Power Station, same-day slots are available seven days a week including evenings.

<div align="center">\n\n
![Same-day GP appointment at LIPS Healthcare](same-day-gp-appointment.webp "600x400")\n\n
</div>

Booking takes minutes. Call or book online your <a href="/private-gp#booking-calendar-widget" target="_blank" rel="noopener noreferrer"><span style="color:#4CCFB8;font-weight:600;text-decoration:underline;">Private GP appointment</span></a>, choose an available time, and attend your appointment the same day. Appointments are confirmed at the time of booking. Most patients from Chelsea, Clapham, Vauxhall, and Pimlico are seen within a few hours of calling, with direct access via the Northern line or London Overground.

*Your appointment covers:*

* A full clinical assessment
* A diagnosis where possible
* A clear plan for what happens next

Whether that is a prescription, a blood test, or a referral to a specialist, you leave with answers, not a follow-up booking request.

<div style="text-align:center;margin:0.5rem 0;"><strong><a href="/private-gp#booking-calendar-widget" target="_blank" rel="noopener noreferrer">Book a same-day GP appointment at LIPS Healthcare</a></strong></div>

<span id="what-can-a-same-day-gp-appointment-treat"></span>

### What can a same-day GP appointment treat?

Same-day private GP appointments cover a wide range of acute and non-acute conditions. Most concerns appropriate for a standard GP visit are appropriate for a same-day private appointment.

*Common reasons patients book a same-day GP appointment in London include:*

<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,300px),1fr));gap:20px;margin:1.5rem 0;width:100%;align-items:stretch;">

  <div style="min-width:0;border-radius:16px;background:#fff;padding:20px;box-shadow:0px 2px 4px 0px #0000001F;">
    <h4 style="margin:0 0 12px;font-size:16px;font-weight:700;color:#1E2A8A;">Infections and illness:</h4>
    <div style="display:flex;flex-wrap:wrap;gap:8px;">
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Sore Throat</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">fever</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">urinary tract infections</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Skin Infections</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Chest Infections</span>
    </div>
  </div>

  <div style="min-width:0;border-radius:16px;background:#fff;padding:20px;box-shadow:0px 2px 4px 0px #0000001F;">
    <h4 style="margin:0 0 12px;font-size:16px;font-weight:700;color:#1E2A8A;">Acute pain</h4>
    <div style="display:flex;flex-wrap:wrap;gap:8px;">
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Back Pain</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Joint Pain</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">sprains</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">musculoskeletal complaints needing prompt assessment</span>
    </div>
  </div>

  <div style="min-width:0;border-radius:16px;background:#fff;padding:20px;box-shadow:0px 2px 4px 0px #0000001F;">
    <h4 style="margin:0 0 12px;font-size:16px;font-weight:700;color:#1E2A8A;">Mental health concerns</h4>
    <div style="display:flex;flex-wrap:wrap;gap:8px;">
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Anxiety</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Low Mood</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Stress-Related Symptoms</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">Requests For Same-Day Mental Health Support Or Referral</span>
    </div>
  </div>

  <div style="min-width:0;border-radius:16px;background:#fff;padding:20px;box-shadow:0px 2px 4px 0px #0000001F;">
    <h4 style="margin:0 0 12px;font-size:16px;font-weight:700;color:#1E2A8A;">Prescriptions</h4>
    <div style="display:flex;flex-wrap:wrap;gap:8px;">
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">private prescriptions for regular medications</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">urgent prescriptions</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">changes to existing treatment</span>
    </div>
  </div>

  <div style="min-width:0;border-radius:16px;background:#fff;padding:20px;box-shadow:0px 2px 4px 0px #0000001F;">
    <h4 style="margin:0 0 12px;font-size:16px;font-weight:700;color:#1E2A8A;">Sick notes</h4>
    <div style="display:flex;flex-wrap:wrap;gap:8px;">
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">private fit notes for employment</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">including same-day issue</span>
    </div>
  </div>

  <div style="min-width:0;border-radius:16px;background:#fff;padding:20px;box-shadow:0px 2px 4px 0px #0000001F;">
    <h4 style="margin:0 0 12px;font-size:16px;font-weight:700;color:#1E2A8A;">Results and follow-up</h4>
    <div style="display:flex;flex-wrap:wrap;gap:8px;">
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">imaging</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">review of blood test results</span>
      <span style="background:#D3D3E9;color:#4F4F4F;border-radius:16px;padding:6px 14px;font-size:14px;font-weight:600;">specialist letters that need clinical interpretation today</span>
    </div>
  </div>

</div>

<div style="text-align:center;margin:2rem 0;"><strong><a href="/private-gp#booking-calendar-widget" target="_blank" rel="noopener noreferrer">Book a same-day GP appointment at LIPS Healthcare</a></strong></div>

If you are unsure whether your concern warrants a same-day appointment, book one. It is always better to have a concern assessed promptly than to wait.

<span id="same-day-gp-vs-walk-in-gp"></span>

### Same-day GP vs walk-in GP: what is the difference?

Both offer fast access to a doctor. The key difference is certainty.

A same-day GP appointment is a confirmed slot at a set time. The clinician has your details before you arrive, the consultation is uninterrupted, and you know exactly when you will be seen. A walk-in visit has no confirmed time. You attend, you wait, and you are seen when a slot opens.

At LIPS Healthcare in Turbine Hall B, Battersea Power Station, same-day appointments are structured consultations with a named physician. There is no queue, no uncertainty about wait times, and no risk of being asked to come back another day. For urgent GP appointment reasons where timing matters, a confirmed slot is the right choice.

<div align="center">\n\n
![Same-day GP vs walk-in GP](same-day-standard-private-gp.webp "600x400")\n\n
</div>

<span id="same-day-telephone-video-home-visits"></span>

### Need to see a GP today but cannot travel? Same-day telephone, video, and home visits are available

A same-day private GP appointment at LIPS Healthcare does not always mean coming into the clinic. Telephone and video consultations are available on the same day for eligible concerns, giving you access to a qualified physician from wherever you are.

For patients managing work commitments, family responsibilities, or any situation where travelling to Battersea Power Station is not straightforward, a same-day virtual appointment removes that barrier entirely. You book online or by phone, confirm whether you would prefer a telephone or video call, and speak to your GP at the confirmed time.

<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,280px),1fr));gap:24px;margin:2rem 0;width:100%;align-items:stretch;">

  <div style="min-width:0;border-radius:16px;background:#fff;padding:24px;box-shadow:0px 2px 4px 0px #0000001F;">
    <div class="mb-4 flex h-[168px] w-full items-center justify-center overflow-hidden [&_span]:flex [&_span]:h-full [&_span]:w-full [&_span]:items-center [&_span]:justify-center [&_span]:overflow-hidden [&_img]:!h-auto [&_img]:!max-h-full [&_img]:!max-w-full [&_img]:object-contain">
      <img src="telephone-and-video-consultations.webp" alt="Telephone and video consultations" title="480x240" />
    </div>
    <h4 style="margin:0 0 12px;font-size:20px;font-weight:700;color:#1E2A8A;text-align:center;">Telephone and video consultations</h4>
    <p style="margin:0 0 10px;font-size:14px;line-height:1.6;text-align:left;">Telephone appointments are well suited to prescription reviews, results follow-up, acute concerns without physical symptoms, and medication queries. Video consultations cover the same scope, with the added ability to show your GP a rash, swelling, or any visible change directly on screen.</p>
  </div>

  <div style="min-width:0;border-radius:16px;background:#fff;padding:24px;box-shadow:0px 2px 4px 0px #0000001F;">
    <div class="mb-4 flex h-[168px] w-full items-center justify-center overflow-hidden [&_span]:flex [&_span]:h-full [&_span]:w-full [&_span]:items-center [&_span]:justify-center [&_span]:overflow-hidden [&_img]:!h-auto [&_img]:!max-h-full [&_img]:!max-w-full [&_img]:object-contain">
      <img src="home-visits.webp" alt="Home visits" title="480x240" />
    </div>
    <h4 style="margin:0 0 12px;font-size:20px;font-weight:700;color:#1E2A8A;text-align:center;">Home visits</h4>
    <p style="margin:0 0 10px;font-size:14px;line-height:1.6;text-align:left;">Home visits are also available for patients who are unwell and unable to leave home. Contact LIPS Healthcare directly to confirm availability for your area.</p>
  </div>

</div>

LIPS Healthcare provides all three formats seven days a week, including evenings, without prior registration or a GP referral. Same-day GP care is available whether you prefer to come in, call in, or have your doctor come to you.

Every GP at LIPS Healthcare holds full GMC registration. The same-day virtual and home visit service connects you to the same qualified physicians who see patients in person at Turbine Hall B, Battersea Power Station, supported by more than 170 specialist consultants on site.


<span id="where-to-get-a-same-day-private-gp-appointment"></span>

### Where to get a same-day private GP appointment in South London

LIPS Healthcare offers same-day private GP appointments in London at Battersea Power Station, first floor, Turbine Hall B. The clinic is accessible from Nine Elms, Wandsworth, Pimlico, Vauxhall, Chelsea, and Clapham via the Northern line or London Overground.

Same-day slots are available seven days a week including weekends and evenings. No registration and no GP referral are required. Patients searching for a private GP appointment near me in South and Central London can typically reach LIPS Healthcare in under 20 minutes.

<div align="center">\n\n
![Where to get a same-day private GP appointment in South London](private-prescription-cost.webp "600x400")\n\n
</div>

On-site blood tests, an in-house pharmacy, and same-day specialist referrals mean most presenting concerns are fully addressed in a single visit.


><span style="font-weight: bold; color: #2A347A;">Medical disclaimer</span>
>
>This article is for informational purposes only. It does not constitute medical advice, a diagnosis, or a treatment recommendation. Always consult a qualified medical professional for any health concern. Pricing ranges are approximate and subject to change. Contact LIPS Healthcare directly for current fees.


<span id="frequently-asked-questions"></span>
`,
      styles: {
        h3: 'text-lg md:text-[32px] font-bold text-theme-primary mb-4',
        h4: 'text-lg md:text-[20px] font-bold text-theme-primary mb-4',
        p: 'text-sm md:text-base text-text-default font-medium leading-relaxed mb-3',
        a: 'font-semibold text-text-action',
        em: 'not-italic font-bold',
        strong:
          'inline-block bg-theme-primary hover:bg-white border border-theme-primary text-white px-6 py-3 rounded-lg font-bold no-underline cursor-pointer my-4 text-center [&_a]:text-white [&_a]:no-underline [&_a]:font-bold hover:[&_a]:text-theme-primary',
        table:
          'w-full table-fixed border-collapse text-xs md:text-sm border-2 border-black',
        th: 'border-2 border-black bg-text-muted-blue px-2 py-2 md:px-4 md:py-3 text-left font-semibold text-theme-primary whitespace-normal wrap-break-word',
        td: 'border-2 border-black px-2 py-2 md:px-4 md:py-3 align-top font-semibold whitespace-normal wrap-break-word',
        ul: 'rich-check-list flex flex-col gap-1',
        li: 'inline-flex w-fit items-center rounded-xl bg-white px-4 py-3 text-base [box-shadow:0px_2px_8px_0px_#00000014]',
        blockquote:
          'border-l-4 border-theme-primary bg-[#E0E7FF] rounded-lg p-4 my-8 text-sm text-text-default not-italic',
      },
      tableOptions: {
        width: '100%',
        columnWidths: ['50%', '50%'],
      },
    },
    faq: {
      heading: {
        content:
          '#### Frequently asked questions about same-day GP appointments in London',
        styles: {
          h4: 'text-xl md:text-2xl font-bold text-theme-primary mb-6',
        },
      },
      items: [
        {
          question: {
            content: 'How do I get a same-day GP appointment in London?',
          },
          answer: {
            content:
              'Book online at lips.org.uk/gp or call LIPS Healthcare at Battersea Power Station directly. Same-day slots are available seven days a week with no registration or GP referral required. Most patients are seen within a few hours of booking.',
          },
        },
        {
          question: {
            content: 'How much does a same-day GP appointment cost in London?',
          },
          answer: {
            content:
              'The same as a standard appointment at most private clinics, between £80 and £200, depending on the clinic and what is included. LIPS Healthcare does not charge a premium for same-day access.',
          },
        },
        {
          question: {
            content: 'What are valid urgent GP appointment reasons?',
          },
          answer: {
            content:
              'Any new or worsening symptom needing same-day clinical assessment qualifies, including infections, acute pain, breathing difficulty, mental health crises, rashes, and any concern you do not feel can wait. If you are experiencing a medical emergency, call 999.',
          },
        },
        {
          question: {
            content: "Can I get a same-day doctor's appointment for a child?",
          },
          answer: {
            content:
              'Yes. LIPS Healthcare offers private paediatric appointments as well as adult GP consultations. Same-day slots for children are available at Battersea Power Station seven days a week, with no prior registration required.',
          },
        },
        {
          question: {
            content: 'Can I get a prescription at a same-day GP appointment?',
          },
          answer: {
            content:
              'Yes. LIPS Healthcare GPs issue private prescriptions at the same visit. The on-site pharmacy at Battersea Power Station means your prescription can be filled immediately after your appointment.',
          },
        },
        {
          question: {
            content:
              "Is a same-day doctor's appointment the same as an urgent appointment?",
          },
          answer: {
            content:
              "Yes, in private practice. A same-day doctor's appointment is booked for concerns requiring prompt attention. At LIPS Healthcare, same-day and urgent GP appointments are the same service: a confirmed slot on the day you call, with a qualified physician.",
          },
        },
        {
          question: {
            content:
              'Do I need to register to book a same-day private GP appointment?',
          },
          answer: {
            content:
              'No. LIPS Healthcare accepts patients without prior registration. You can book a same-day GP appointment in London as a new patient and be seen on the same day, with no referral and no paperwork to complete before your visit.',
          },
        },
        {
          question: {
            content:
              'Can I have a same-day GP appointment by telephone or video?',
          },
          answer: {
            content:
              'Yes. LIPS Healthcare offers same-day telephone and video consultations for eligible concerns. If attending in person is not possible, contact the clinic to confirm whether a virtual appointment suits your situation. All virtual formats are available seven days a week.',
          },
        },
        {
          question: {
            content: 'Do LIPS Healthcare GPs do home visits?',
          },
          answer: {
            content:
              'Yes. Home visits are available for patients who are unwell and unable to travel to Battersea Power Station. Contact LIPS Healthcare directly to check availability and arrange a same-day visit.',
          },
        },
      ],
    },
  },
];
